import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextareaAutosize from "react-textarea-autosize";
import {
  Loader2,
  MessageSquarePlus,
  Plus,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  BucketBadge,
  formatBucket,
} from "@/components/settings/training-teen-bucket-badge";
import { TrainingTeenPlayground } from "@/components/settings/training-teen-playground";
import {
  conversationBuckets,
  managedProfileKeys,
  TrainingContentLevel,
  TrainingContentModule,
  TrainingContentTeen,
  TrainingTeenProfile,
  useUpdateTrainingTeenContent,
} from "@/lib/api/training-simulator-content";
import { cn, getInitials } from "@/lib/utils";

const managedKeySet = new Set<string>(managedProfileKeys);

type AdditionalProfileResult =
  | { value: Record<string, unknown>; error?: never }
  | { value?: never; error: string };

const parseAdditionalProfile = (raw: string): AdditionalProfileResult => {
  const trimmed = raw.trim();
  if (!trimmed) return { value: {} };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { error: "Not valid JSON — fix it before saving." };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { error: 'Must be a JSON object, e.g. { "key": "value" }.' };
  }

  const reservedKeys = Object.keys(parsed).filter((key) =>
    managedKeySet.has(key),
  );
  if (reservedKeys.length > 0) {
    return {
      error: `These fields are managed by the form above and would be overwritten: ${reservedKeys.join(", ")}. Remove them from the JSON.`,
    };
  }

  return { value: parsed as Record<string, unknown> };
};

const teenFormSchema = z.object({
  display_name: z.string().trim().min(1, "Display name is required."),
  age: z
    .string()
    .trim()
    .min(1, "Age is required.")
    .regex(/^\d+$/, "Age must be a whole number."),
  gender: z.string(),
  pronouns: z.string(),
  conversation_bucket: z.enum(conversationBuckets),
  persona_prompt: z.string(),
  summary: z.string(),
  vibe: z.string(),
  closestPerson: z.string(),
  interests: z.string(),
  struggles: z.string(),
  proudMoments: z.string(),
  initial_messages: z.array(
    z.object({
      sender_type: z.enum(["mentor", "simulated_teen"]),
      content: z.string(),
    }),
  ),
  additionalProfileJson: z.string().superRefine((value, ctx) => {
    const result = parseAdditionalProfile(value);
    if (result.error) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.error });
    }
  }),
});

type TeenFormValues = z.infer<typeof teenFormSchema>;

const toLines = (value: unknown): string =>
  Array.isArray(value) ? value.filter(Boolean).join("\n") : "";

const fromLines = (value: string): string[] =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const getAdditionalProfile = (profile: TrainingTeenProfile) =>
  Object.fromEntries(
    Object.entries(profile).filter(([key]) => !managedKeySet.has(key)),
  );

const buildFormValues = (teen: TrainingContentTeen): TeenFormValues => {
  const profile = teen.profile ?? {};

  return {
    display_name: teen.display_name,
    age: String(teen.age),
    gender: teen.gender ?? "",
    pronouns: teen.pronouns ?? "",
    conversation_bucket: teen.conversation_bucket,
    persona_prompt: teen.persona_prompt ?? "",
    summary: typeof profile.summary === "string" ? profile.summary : "",
    vibe: typeof profile.vibe === "string" ? profile.vibe : "",
    closestPerson:
      typeof profile.closestPerson === "string" ? profile.closestPerson : "",
    interests: toLines(profile.interests),
    struggles: toLines(profile.struggles),
    proudMoments: toLines(profile.proudMoments),
    initial_messages: Array.isArray(profile.initial_messages)
      ? profile.initial_messages
      : [],
    additionalProfileJson: JSON.stringify(
      getAdditionalProfile(profile),
      null,
      2,
    ),
  };
};

interface TrainingTeenEditorProps {
  module: TrainingContentModule;
  level: TrainingContentLevel;
  teen: TrainingContentTeen;
  onDirtyChange: (dirty: boolean) => void;
}

// Mount with key={teen.id} so switching teens remounts with fresh defaults.
export function TrainingTeenEditor({
  module,
  level,
  teen,
  onDirtyChange,
}: TrainingTeenEditorProps) {
  const updateTeen = useUpdateTrainingTeenContent();
  const form = useForm<TeenFormValues>({
    resolver: zodResolver(teenFormSchema),
    defaultValues: buildFormValues(teen),
    mode: "onChange",
  });
  const initialMessages = useFieldArray({
    control: form.control,
    name: "initial_messages",
  });
  const { isDirty } = form.formState;
  const [savedInitialMessages, setSavedInitialMessages] = useState(
    buildFormValues(teen).initial_messages,
  );

  useEffect(() => {
    onDirtyChange(isDirty);
    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  const onSubmit = async (values: TeenFormValues) => {
    const additionalProfile =
      parseAdditionalProfile(values.additionalProfileJson).value ?? {};

    let saved: TrainingContentTeen;
    try {
      saved = await updateTeen.mutateAsync({
        teenId: teen.id,
        payload: {
          display_name: values.display_name,
          age: Number.parseInt(values.age, 10),
          gender: values.gender.trim() || null,
          pronouns: values.pronouns.trim() || null,
          conversation_bucket: values.conversation_bucket,
          persona_prompt: values.persona_prompt.trim() || null,
          profile: {
            ...additionalProfile,
            summary: values.summary.trim() || null,
            vibe: values.vibe.trim() || null,
            closestPerson: values.closestPerson.trim() || null,
            interests: fromLines(values.interests),
            struggles: fromLines(values.struggles),
            proudMoments: fromLines(values.proudMoments),
            initial_messages: values.initial_messages
              .map((message) => ({
                sender_type: message.sender_type,
                content: message.content.trim(),
              }))
              .filter((message) => message.content.length > 0),
          },
        },
      });
    } catch {
      // The mutation hook already surfaced an error toast; keep edits for retry.
      return;
    }

    form.reset(buildFormValues(saved));
    setSavedInitialMessages(buildFormValues(saved).initial_messages);
  };

  return (
    <Form {...form}>
      <form
        className="min-w-0 space-y-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Card className="sticky top-0 z-10 bg-white/95 backdrop-blur">
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold uppercase text-white">
              {getInitials(teen.display_name) || "?"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-lg font-bold">
                  {teen.display_name}
                </h2>
                <BucketBadge bucket={teen.conversation_bucket} />
              </div>
              <p className="truncate text-sm text-gray-500">
                {module.title} · {level.title}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isDirty && (
                <span className="hidden text-xs font-medium text-amber-600 sm:inline">
                  Unsaved changes
                </span>
              )}
              {isDirty && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  disabled={updateTeen.isPending}
                >
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Reset
                </Button>
              )}
              <Button type="submit" disabled={!isDirty || updateTeen.isPending}>
                {updateTeen.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-1 h-4 w-4" />
                )}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="configuration" className="space-y-4">
          <TabsList>
            <TabsTrigger value="configuration">Configuration</TabsTrigger>
            <TabsTrigger value="playground">Playground</TabsTrigger>
          </TabsList>

          <TabsContent value="configuration" className="space-y-4">
            <Section
              title="Basics"
              description="Core identity shown to mentors in the simulator."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="display_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Display Name
                        <span className="ml-0.5 text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Age<span className="ml-0.5 text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input type="number" min={1} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Female" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pronouns"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pronouns</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. she/her" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="conversation_bucket"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Conversation Bucket</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {conversationBuckets.map((bucket) => (
                            <SelectItem key={bucket} value={bucket}>
                              {formatBucket(bucket)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Where this conversation appears in the mentor&apos;s
                        inbox.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="closestPerson"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Closest Person</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Older sister" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Section>

            <Section
              title="Persona"
              description="How this teen thinks, writes, and responds during a session."
            >
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="persona_prompt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Persona Prompt</FormLabel>
                      <FormControl>
                        <TextareaAutosize
                          className="w-full rounded-md border border-gray-200 p-3 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          minRows={8}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The full system prompt that drives this teen&apos;s
                        behavior.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="summary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Summary</FormLabel>
                        <FormControl>
                          <Textarea rows={3} {...field} />
                        </FormControl>
                        <FormDescription>
                          A one-or-two sentence overview of who this teen is.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="vibe"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vibe</FormLabel>
                        <FormControl>
                          <Textarea rows={3} {...field} />
                        </FormControl>
                        <FormDescription>
                          The overall energy or tone of their messages.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </Section>

            <Section
              title="Profile Details"
              description="Background facts the simulator can draw from. One item per line."
            >
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="interests"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interests</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={5}
                          placeholder={"Drawing\nSoccer\nBaking"}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>One per line</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="struggles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Struggles</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={5}
                          placeholder={"Anxiety about college\nMaking friends"}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>One per line</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="proudMoments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Proud Moments</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={5}
                          placeholder={"Made varsity team\nGot first job"}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>One per line</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Section>

            <Section
              title="Initial Messages"
              description="Scripted messages that seed the conversation before the mentor's first reply."
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    initialMessages.append({
                      sender_type: "simulated_teen",
                      content: "",
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Message
                </Button>
              }
            >
              {initialMessages.fields.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-gray-200 px-4 py-8 text-center">
                  <MessageSquarePlus className="h-6 w-6 text-gray-300" />
                  <p className="text-sm text-gray-500">
                    No scripted messages yet — the conversation will start
                    empty.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {initialMessages.fields.map((messageField, index) => (
                    <div
                      key={messageField.id}
                      className="grid gap-2 rounded-md border border-gray-100 bg-gray-50/50 p-3 md:grid-cols-[170px_minmax(0,1fr)_auto]"
                    >
                      <FormField
                        control={form.control}
                        name={`initial_messages.${index}.sender_type`}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="simulated_teen">
                                Simulated Teen
                              </SelectItem>
                              <SelectItem value="mentor">Mentor</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`initial_messages.${index}.content`}
                        render={({ field }) => (
                          <TextareaAutosize
                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            minRows={1}
                            placeholder="Message content..."
                            {...field}
                          />
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-gray-400 hover:text-red-600"
                        aria-label="Remove message"
                        onClick={() => initialMessages.remove(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section
              title="Advanced"
              description="Extra profile fields stored as JSON, for anything not covered above."
            >
              <FormField
                control={form.control}
                name="additionalProfileJson"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Extra Profile JSON</FormLabel>
                    <FormControl>
                      <TextareaAutosize
                        className={cn(
                          "w-full rounded-md border p-3 font-mono text-sm focus-visible:outline-none focus-visible:ring-2",
                          fieldState.error
                            ? "border-red-300 focus-visible:ring-red-500"
                            : "border-gray-200 focus-visible:ring-blue-500",
                        )}
                        minRows={6}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>
          </TabsContent>

          <TabsContent value="playground">
            <TrainingTeenPlayground
              teenId={teen.id}
              isDirty={isDirty}
              initialMessages={savedInitialMessages}
            />
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
}

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0 p-4 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base">{title}</CardTitle>
          {description && (
            <CardDescription className="text-xs">{description}</CardDescription>
          )}
        </div>
        {action}
      </CardHeader>
      <CardContent className="p-4 pt-0">{children}</CardContent>
    </Card>
  );
}
